import { useEffect, useState, useMemo } from 'react'
import { productsApi, sellerProductsApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

interface Product {
  id: number
  name: string
  description: string | null
  presentation: string
  recommended_price: number
  category: 'A' | 'B' | 'C'
}

interface SellerProduct {
  id: number
  product_id: number
  name: string
  description: string | null
  presentation: string
  recommended_price: number
  category: string
  real_price: number
}

const categoryLabels: Record<string, string> = {
  A: 'Premium',
  B: 'Estándar',
  C: 'Económico',
}

export default function ProductsPage() {
  const [catalog, setCatalog] = useState<Product[]>([])
  const [myProducts, setMyProducts] = useState<SellerProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [addingProductId, setAddingProductId] = useState<number | null>(null)
  const [addPrice, setAddPrice] = useState('')
  const [addError, setAddError] = useState('')
  const [addSaving, setAddSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editPrice, setEditPrice] = useState('')
  const [editError, setEditError] = useState('')
  const [editSaving, setEditSaving] = useState(false)

  const fetchData = async () => {
    try {
      const [catalogRes, myRes] = await Promise.all([
        productsApi.list(),
        sellerProductsApi.list(),
      ])
      setCatalog(catalogRes.data.products || [])
      setMyProducts(myRes.data.seller_products || [])
    } catch (err) {
      console.error('Error fetching products:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const myProductIds = new Set(myProducts.map((sp) => sp.product_id))
  const availableProducts = catalog.filter((p) => !myProductIds.has(p.id))

  // Margin isn't a real column on the API row -- compute it once here so
  // sorting by it has an actual property to compare, instead of recomputing
  // it inline per-row in the JSX.
  const myProductsWithMargin = useMemo(
    () => myProducts.map((sp) => ({ ...sp, margin: sp.real_price - sp.recommended_price })),
    [myProducts]
  )

  const {
    sortedData: sortedMyProducts,
    sortConfig: myProductsSortConfig,
    requestSort: requestMyProductsSort,
  } = useSortableData(myProductsWithMargin)

  const {
    sortedData: sortedAvailableProducts,
    sortConfig: availableSortConfig,
    requestSort: requestAvailableSort,
  } = useSortableData(availableProducts)

  const openAdd = (product: Product) => {
    setAddingProductId(product.id)
    setAddPrice('')
    setAddError('')
    setShowAddModal(true)
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!addPrice || parseFloat(addPrice) < 0) {
      setAddError('Por favor ingresa un precio válido')
      return
    }
    setAddSaving(true)
    setAddError('')
    try {
      await sellerProductsApi.add({
        product_id: addingProductId,
        real_price: parseFloat(addPrice),
      })
      setShowAddModal(false)
      fetchData()
    } catch (err: any) {
      setAddError(err.response?.data?.error || 'Error al agregar producto')
    } finally {
      setAddSaving(false)
    }
  }

  const openEdit = (sp: SellerProduct) => {
    setEditingId(sp.id)
    setEditPrice(String(sp.real_price))
    setEditError('')
  }

  const handleEditSave = async (sp_id: number) => {
    if (!editPrice || parseFloat(editPrice) < 0) {
      setEditError('Por favor ingresa un precio válido')
      return
    }
    setEditSaving(true)
    setEditError('')
    try {
      await sellerProductsApi.update(sp_id, { real_price: parseFloat(editPrice) })
      setEditingId(null)
      fetchData()
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Error al actualizar precio')
    } finally {
      setEditSaving(false)
    }
  }

  const handleRemove = async (sp_id: number) => {
    if (!confirm('¿Eliminar este producto de tu catálogo?')) return
    try {
      await sellerProductsApi.remove(sp_id)
      fetchData()
    } catch (err) {
      console.error('Error removing product:', err)
    }
  }

  const addingProduct = catalog.find((p) => p.id === addingProductId)

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-2">Mis Productos</h1>
      <p className="text-sm text-espresso-500 mb-6">
        Selecciona productos del catálogo y establece tu precio de venta. Tu margen = tu precio - precio recomendado.
      </p>

      {/* My Products */}
      <div className="bg-cream-50 shadow rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-espresso-200">
          <h2 className="text-sm font-semibold text-espresso-700 uppercase tracking-wide">Mis Productos Activos</h2>
        </div>
        {myProducts.length === 0 ? (
          <div className="p-6 text-center text-espresso-500">
            No has agregado productos aún. Explora el catálogo abajo para comenzar.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Producto" sortKey="name" sortConfig={myProductsSortConfig} onSort={requestMyProductsSort} />
                  <SortableTh label="Presentación" sortKey="presentation" sortConfig={myProductsSortConfig} onSort={requestMyProductsSort} />
                  <SortableTh label="Precio Rec." sortKey="recommended_price" sortConfig={myProductsSortConfig} onSort={requestMyProductsSort} />
                  <SortableTh label="Tu Precio" sortKey="real_price" sortConfig={myProductsSortConfig} onSort={requestMyProductsSort} />
                  <SortableTh label="Margen" sortKey="margin" sortConfig={myProductsSortConfig} onSort={requestMyProductsSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedMyProducts.map((sp) => {
                  const margin = sp.margin
                  const marginPct = sp.recommended_price > 0
                    ? ((margin / sp.recommended_price) * 100).toFixed(0)
                    : '0'
                  return (
                    <tr key={sp.id} className="hover:bg-cream-100">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-espresso-800">{sp.name}</div>
                        {sp.description && (
                          <div className="text-xs text-espresso-500 max-w-xs truncate">{sp.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                        {sp.presentation}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                        ${sp.recommended_price.toFixed(2)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {editingId === sp.id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={editPrice}
                              onChange={(e) => setEditPrice(e.target.value)}
                              step="0.01"
                              min="0"
                              className="w-24 border border-espresso-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                            />
                            <button
                              onClick={() => handleEditSave(sp.id)}
                              disabled={editSaving}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              Guardar
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="text-espresso-500 hover:text-espresso-700 text-sm"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-green-700">
                            ${sp.real_price.toFixed(2)}
                          </span>
                        )}
                        {editingId !== sp.id && editError && editingId === sp.id && (
                          <div className="text-xs text-red-600 mt-1">{editError}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          +${margin.toFixed(2)} ({marginPct}%)
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                        {editingId !== sp.id && (
                          <div className="flex items-center justify-end gap-1">
                            <TableActionButton onClick={() => openEdit(sp)} variant="primary">
                              Editar Precio
                            </TableActionButton>
                            <TableActionButton onClick={() => handleRemove(sp.id)} variant="danger">
                              Eliminar
                            </TableActionButton>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Available Catalog */}
      <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-espresso-200">
          <h2 className="text-sm font-semibold text-espresso-700 uppercase tracking-wide">Productos Disponibles</h2>
        </div>
        {availableProducts.length === 0 ? (
          <div className="p-6 text-center text-espresso-500">
            Todos los productos del catálogo han sido agregados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Producto" sortKey="name" sortConfig={availableSortConfig} onSort={requestAvailableSort} />
                  <SortableTh label="Presentación" sortKey="presentation" sortConfig={availableSortConfig} onSort={requestAvailableSort} />
                  <SortableTh label="Categoría" sortKey="category" sortConfig={availableSortConfig} onSort={requestAvailableSort} />
                  <SortableTh label="Precio Rec." sortKey="recommended_price" sortConfig={availableSortConfig} onSort={requestAvailableSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acción</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedAvailableProducts.map((p) => (
                  <tr key={p.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-espresso-800">{p.name}</div>
                      {p.description && (
                        <div className="text-xs text-espresso-500 max-w-xs truncate">{p.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {p.presentation}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        p.category === 'A'
                          ? 'bg-terracotta-100 text-terracotta-800'
                          : p.category === 'B'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-cream-200 text-espresso-700'
                      }`}>
                        {categoryLabels[p.category] || p.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      ${p.recommended_price.toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => openAdd(p)}
                        className="px-3 py-1 bg-terracotta-600 text-white rounded-md text-xs font-medium hover:bg-terracotta-700"
                      >
                        + Agregar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Price Modal */}
      {showAddModal && addingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Agregar Producto</h2>
            </div>
            <form onSubmit={handleAdd} className="px-6 py-4 space-y-4">
              {addError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{addError}</div>
              )}
              <div className="bg-cream-50 rounded-lg p-4">
                <div className="text-sm font-medium text-espresso-800">{addingProduct.name}</div>
                <div className="text-xs text-espresso-500">{addingProduct.presentation}</div>
                <div className="text-xs text-espresso-500 mt-1">
                  Precio recomendado: <span className="font-semibold text-green-700">${addingProduct.recommended_price.toFixed(2)}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Tu Precio de Venta *</label>
                <input
                  type="number"
                  value={addPrice}
                  onChange={(e) => setAddPrice(e.target.value)}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  autoFocus
                  required
                />
                <p className="text-xs text-espresso-500 mt-1">
                  Establece el precio que pagarán tus clientes. Margen = Tu Precio - ${addingProduct.recommended_price.toFixed(2)}
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={addSaving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {addSaving ? 'Agregando...' : 'Agregar Producto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
